import {
  ArrowUpOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  PoundOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { App, Button, Col, Flex, Popover, Row, Switch, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useSWRConfig } from "swr";
import { Link } from "react-router-dom";
import Card from "src/components/Card";
import CurrencyAmount from "src/components/CurrencyAmount";
import TransactionsTable from "src/components/Transactions/TransactionsTable";
import Loading from "src/components/loading";
import WalletBanner from "src/components/wallet/WalletBanner";
import { WalletBalanceDisplay } from "src/components/wallet/WalletBalanceDisplay";
import { EarlyPaymentInfoModal } from "src/components/wallet/EarlyPaymentInfo";
import FirstEarlyPaymentBadge from "src/components/wallet/FirstEarlyPaymentBadge";
import useEarlyPaymentStatus from "src/hooks/apis/useEarlyPaymentStatus";
import useEarlyPaymentToggle from "src/hooks/apis/useEarlyPaymentToggle";
import useLoanEligibility from "src/hooks/apis/useLoanEligibility";
import useLoans from "src/hooks/apis/useLoans";
import useOrg from "src/hooks/apis/useOrg";
import useTransactions from "src/hooks/apis/useTransactions";
import useUser from "src/hooks/useUser";
import useOrgTerminology from "src/hooks/useOrgTerminology";
import { IS_WHITE_LABEL } from "src/constants";

function Finance() {
  const { message } = App.useApp();
  const { mutate } = useSWRConfig();
  const [epInfoOpen, setEpInfoOpen] = useState(false);
  const { data: user, isLoading: loading, mutate: refetchUserData } = useUser();
  const { data: org, isLoading: orgLoading } = useOrg();
  const isWhiteLabel = org?.config?.whiteLabel ?? IS_WHITE_LABEL;
  const terminology = useOrgTerminology();
  const { data: epStatus } = useEarlyPaymentStatus();
  const epToggle = useEarlyPaymentToggle();
  const { data: recentTransactions } = useTransactions({ current: 1, pageSize: 15 });
  const loanEnabled = !!org?.config?.loanConfig?.enabled;
  const { data: loans } = useLoans();
  const { data: eligibility } = useLoanEligibility();
  const pendingEpLoan = epStatus?.hasOpenLoan && epStatus.openEarlyPayment?.status === 'PENDING' ? epStatus.openEarlyPayment : null;

  const handleEpToggle = async (checked) => {
    try {
      await epToggle.trigger({ enabled: checked });
      message.success(
        checked ? "پرداخت زودهنگام خودکار فعال شد" : "پرداخت زودهنگام خودکار غیرفعال شد"
      );
      // Auto EP and loans are mutually exclusive, so the loan banners depend on
      // this flag — refresh eligibility/loans too, not just the EP status.
      mutate("early-payment-status");
      mutate("loan-eligibility");
      mutate("loans");
    } catch {
      message.error("خطا در تغییر تنظیمات");
    }
  };

  if (!user || loading || orgLoading) {
    return <Loading size="fill" text="درحال بارگذاری..." />;
  }

  const gbpToIrt = org?.config?.gbpToIrt ?? 0;
  const gbpCredit = (user?.gbpCredit ?? 0) + (pendingEpLoan?.principal || 0);
  const epAvailable = epStatus?.available && (epStatus?.estimatedAmount ?? 0) > 0;
  const epEstimatedGbp = epStatus?.estimatedAmount ?? 0;

  const pendingGbpAdjustment =
    -1 *
    (recentTransactions?.data ?? [])
      .filter((t) => t.status === "PENDING")
      .reduce((sum, t) => sum + (t.exchangedAmount?.gbp ?? 0), 0);

  const combinedGbp = (epAvailable ? gbpCredit + epEstimatedGbp : gbpCredit) + pendingGbpAdjustment;
  const combinedIrt = combinedGbp * gbpToIrt;
  const creditIrt = gbpCredit * gbpToIrt;
  const epIrt = epEstimatedGbp * gbpToIrt;
  const pendingIrt = -1 * pendingGbpAdjustment * gbpToIrt;

  console.log({
    pendingGbpAdjustment,
    combinedGbp,
    gbpCredit,
  });
  // Loan banner logic
  const openLoan = loans?.find((l) => l.status === "ACTIVE" || l.status === "PENDING");
  const loanEligible = eligibility?.eligible ?? false;
  const loanLimit = eligibility?.limit ?? 0;
  // An open early-payment advance isn't a "loan you requested" — it just blocks
  // requesting a standard loan until it's settled. Show a disabled banner for it.
  const openLoanIsEarlyPayment = openLoan?.type === "EARLY_PAYMENT";
  const loanBanned = eligibility?.banned ?? false;
  const autoEpOn = !!epStatus?.autoEnabled;
  const showLoanCta = loanEnabled && !openLoan && loanEligible && loanLimit > 0 && !autoEpOn;
  const showLoanStatus = loanEnabled && !!openLoan && !openLoanIsEarlyPayment;
  const showLoanBlockedByEp = loanEnabled && !!openLoan && openLoanIsEarlyPayment;
  // Auto early payment is mutually exclusive with standard loans: when it's on
  // (and there's no open loan) the loan request is disabled.
  const showLoanBlockedByAutoEp =
    loanEnabled && !openLoan && autoEpOn && !loanBanned && loanLimit > 0;

  const LOAN_STATUS_LABELS = {
    PENDING: { label: "در انتظار تأیید", color: "warning", icon: <ClockCircleOutlined /> },
    ACTIVE: { label: "فعال", color: "success", icon: <CheckCircleOutlined /> },
  };

  // CTA reads "available" (blue); a pending loan reads amber, an active one emerald.
  const statusBannerTheme = openLoan?.status === "PENDING" ? "amber" : "emerald";

  const withdrawalAction = org?.config.withdrawalsEnabled && (
    <Popover
      content={
        <div className="max-w-xs text-right">
          <Typography.Text>
            حداقل اعتبار مورد نیاز برای برداشت <CurrencyAmount amount={30} currency="GBP" />
            می‌باشد.
            {!pendingEpLoan && (
              <>
                <br />
                اعتبار فعلی شما: <CurrencyAmount amount={user?.gbpCredit || 0} currency="GBP" />
              </>
            )}
          </Typography.Text>
        </div>
      }
      title="برداشت غیرفعال است"
      trigger={user.gbpCredit < 30 && !epAvailable ? ["hover", "click"] : []}
    >
      <Link to="/finance/withdraw">
        <Button
          disabled={user.gbpCredit < 30 && !epAvailable}
          icon={<ArrowUpOutlined className="text-red-600" />}
        >
          {terminology.withdrawButtonLabel}
        </Button>
      </Link>
    </Popover>
  );

  return (
    <>
      {/* Balance card — full width */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title="اعتبار فعلی"
            Icon={PoundOutlined}
            className={isWhiteLabel ? "w-full" : "max-w-[100%] md:max-w-[440px] lg:max-w-[520px]"}
            actions={!isWhiteLabel && withdrawalAction}
          >
            <Flex vertical gap={4}>
              <WalletBalanceDisplay
                primaryAmount={combinedIrt.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                primaryUnit="تومان"
                heroColor={isWhiteLabel ? org?.theme?.walletHeroColor || org?.theme?.primaryColor : undefined}
              >
                <div className="mt-3">
                  {/* Credit + early payment together on one small, muted row;
                      each label sits above its value */}
                  <Flex gap={32} wrap="wrap">
                    {
                      !openLoan && epAvailable && (
                    <div>
                      <Typography.Text type="secondary" className="block text-xs">
                        دارایی
                      </Typography.Text>
                      <CurrencyAmount amount={Math.floor(creditIrt)} currency="IRT" fontSize={13} />
                    </div>
                      )
                    }
      

                    {epAvailable && (
                      <div>
                        <Typography.Text type="secondary" className="block text-xs">
                          <ThunderboltOutlined className="ml-1 text-amber-500" />
                          پرداخت زودهنگام
                          <Tooltip title="درباره‌ی پرداخت زودهنگام">
                            <InfoCircleOutlined
                              className="mr-1 cursor-pointer opacity-60 hover:opacity-100"
                              onClick={() => setEpInfoOpen(true)}
                            />
                          </Tooltip>
                        </Typography.Text>
                        <CurrencyAmount amount={Math.floor(epIrt)} currency="IRT" fontSize={13} />
                        <FirstEarlyPaymentBadge
                          className="mx-2 mt-1"
                          onClick={() => setEpInfoOpen(true)}
                        />
                      </div>
                    )}
                    {pendingGbpAdjustment !== 0 && (
                      <div>
                        <Typography.Text type="secondary" className="block text-xs">
                          <ClockCircleOutlined className="ml-1 text-orange-400" />
                          در انتظار
                        </Typography.Text>
                        <CurrencyAmount
                          amount={Math.abs(Math.floor(pendingIrt))}
                          currency="IRT"
                          fontSize={13}
                          color={pendingGbpAdjustment > 0 ? "crimson" : "green"}
                        />
                      </div>
                    )}
                  </Flex>

                  {/* Total below, label above value */}
                  <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-2">
                    <Typography.Text type="secondary" className="block text-xs">
                     

                      {openLoan ? 'معادل پوند':' مجموع (معادل پوند)'}
                    </Typography.Text>
                    <CurrencyAmount amount={combinedGbp} currency="GBP" fontSize={16} />
                  </div>
                </div>
              </WalletBalanceDisplay>
            </Flex>
            {isWhiteLabel && withdrawalAction && (
              <Flex justify="start" className="mt-6">
                {withdrawalAction}
              </Flex>
            )}
          </Card>
        </Col>
      </Row>

      {/* Auto early-payment toggle — banner under the loan banner */}
      {epStatus?.enabled && !loanBanned && !openLoan && (
        <WalletBanner
          className="mt-4"
          theme={epStatus?.autoEnabled ? "violet" : "slate"}
          Icon={ThunderboltOutlined}
          action={
            <Switch
              checkedChildren={
                <CheckOutlined style={{ position: "relative", right: -3, top: 1 }} />
              }
              unCheckedChildren={
                <CloseOutlined style={{ position: "relative", left: -3, top: 1 }} />
              }
              checked={!!epStatus?.autoEnabled}
              loading={epToggle.isMutating}
              onChange={handleEpToggle}
              className="scale-150 mx-3"
              style={{ backgroundColor: epStatus?.autoEnabled ? "#22c55e" : undefined }}
            />
          }
        >
          <div>
            <Flex align="center" gap={6} className="mb-0.5">
              <Typography.Text strong>پرداخت زودهنگام خودکار ماهانه</Typography.Text>
              <Button
                type="text"
                size="small"
                className="p-0 h-auto"
                icon={<InfoCircleOutlined />}
                onClick={() => setEpInfoOpen(true)}
              >
                راهنما
              </Button>
            </Flex>
            <Typography.Text type="secondary" className="text-xs block">
              با فعال‌سازی، هر ماه بخشی از درآمد آینده به‌صورت خودکار به‌عنوان پرداخت زودهنگام به
              اعتبار شما اضافه می‌شود.
            </Typography.Text>
          </div>
        </WalletBanner>
      )}

      <EarlyPaymentInfoModal
        open={epInfoOpen}
        onClose={() => setEpInfoOpen(false)}
        feePercent={epStatus?.feePercent}
        percent={epStatus?.percent}
        firstFeePercent={epStatus?.firstEarlyPaymentFeePercent}
        firstEligible={epStatus?.firstEarlyPaymentEligible}
      />

      {/* Loan CTA banner — wide, full width, below balance card */}
      {showLoanCta && (
        <WalletBanner
          className="mt-4"
          theme="blue"
          Icon={BankOutlined}
          action={
            <Link to="/finance/advance">
              <Button icon={<BankOutlined className="text-red-600" />}>
                دریافت وام
              </Button>
            </Link>
          }
        >
          <div>
            <Typography.Text strong className="block">
              وام در دسترس است
            </Typography.Text>
            <Typography.Text type="secondary" className="text-xs block">
              تا سقف زیر می‌توانید وام دریافت کنید؛ بازپرداخت خودکار از درآمد کانال شما کسر می‌شود.
            </Typography.Text>
            <div className="mt-1">
              <CurrencyAmount
                amount={Math.floor(loanLimit * gbpToIrt)}
                currency="IRT"
                fontSize={24}
                fontWeight={700}
              />
            </div>
          </div>
        </WalletBanner>
      )}

      {/* Loan status banner — wide, full width, tinted by loan state */}
      {showLoanStatus && (
        <WalletBanner
          className="mt-4"
          theme={statusBannerTheme}
          Icon={BankOutlined}
          action={
            <Link to="/finance/advance">
              <Button icon={<InfoCircleOutlined  />}>جزئیات</Button>
            </Link>
          }
        >
          <div>
                <Typography.Text type="secondary" className="text-xs block mb-1">
          وضعیت وام
            </Typography.Text>
             <Tag
                icon={LOAN_STATUS_LABELS[openLoan.status]?.icon}
                color={LOAN_STATUS_LABELS[openLoan.status]?.color}
              >
                {LOAN_STATUS_LABELS[openLoan.status]?.label ?? openLoan.status}
              </Tag>
              </div>
          {openLoan.status === "ACTIVE" && openLoan.balance < 0 && (
            <Flex gap={16} wrap="wrap">
              <div>
                <Typography.Text type="secondary" className="text-xs block mb-1">
                  کل بازپرداخت
                </Typography.Text>
                <CurrencyAmount amount={openLoan.totalPayable} currency="GBP" fontSize={16} />
              </div>
              <div>
                <Typography.Text type="secondary" className="text-xs block mb-1">
                  پرداخت شده
                </Typography.Text>
                <CurrencyAmount
                  amount={openLoan.totalPayable + openLoan.balance}
                  currency="GBP"
                  fontSize={16}
                  color="#52c41a"
                />
              </div>
            </Flex>
          )}
        </WalletBanner>
      )}

      {/* Loan request blocked — user has an active/pending early-payment advance */}
      {showLoanBlockedByEp && (
        <WalletBanner
          className="mt-4"
          theme="slate"
          Icon={BankOutlined}
          action={
            <Button size="large" disabled icon={<BankOutlined />}>
              دریافت وام
            </Button>
          }
        >
          <div>
            <Typography.Text strong className="block">
              درخواست وام در دسترس نیست
            </Typography.Text>
            <Typography.Text type="secondary" className="text-xs block">
              به دلیل داشتن پرداخت زودهنگام فعال، در حال حاضر امکان درخواست وام جدید وجود ندارد. پس
              از تسویه پرداخت زودهنگام می‌توانید درخواست دهید.
            </Typography.Text>
          </div>
        </WalletBanner>
      )}

      {/* Loan request blocked — auto early payment is enabled (mutually exclusive) */}
      {showLoanBlockedByAutoEp && (
        <WalletBanner
          className="mt-4"
          theme="slate"
          Icon={BankOutlined}
          action={
            <Button size="large" disabled icon={<BankOutlined />}>
              دریافت وام
            </Button>
          }
        >
          <div>
            <Typography.Text strong className="block">
              درخواست وام در دسترس نیست
            </Typography.Text>
            <Typography.Text type="secondary" className="text-xs block">
              پرداخت زودهنگام خودکار فعال است. برای درخواست وام، ابتدا پرداخت زودهنگام خودکار را
              غیرفعال کنید.
            </Typography.Text>
          </div>
        </WalletBanner>
      )}

      <Card title="تراکنش ها" Icon={UnorderedListOutlined} className="mt-10">
        <TransactionsTable />
      </Card>

      <style>{"@media print {.print-hide,#root{display: none;}}"}</style>
    </>
  );
}

export default Finance;
